import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, Input, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { HelpText } from '../../../model/help-text';
import { InputControlBase } from '../../forms/input-control.base';
import { AstralPopoverComponent } from '../astral-popover/astral-popover.component';
import { shouldShowAstralValidationError } from '../astral-validation-state';
import { AstralFieldAccessibilityDirective, getAstralFieldErrorId, getAstralFieldHelpId } from '../astral-field-accessibility.util';

@Component({
  selector: 'astral-toggle',
  standalone: true,
  imports: [ReactiveFormsModule, TranslatePipe, AstralPopoverComponent, AstralFieldAccessibilityDirective],
  templateUrl: './astral-toggle.component.html',
  styleUrls: ['./astral-toggle.component.scss'],
})
export class AstralToggleComponent extends InputControlBase<boolean> implements AfterViewInit {
  private static nextId = 0;
  private readonly uid = `astral-toggle-${++AstralToggleComponent.nextId}`;

  constructor(private readonly cd: ChangeDetectorRef) {
    super();
  }

  @Input() focused = false;
  @Input() inputId: string;
  @Input() inputName: string;
  @Input() showCaption = true;
  @Input() span: number | 'full' = 1;

  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  isFocused = false;
  private controlSubscription = new Subscription();

  @HostBinding('tabindex') tabindex = -1;

  @HostBinding('style.grid-column')
  get hostGridColumn(): string | null {
    if (this.span === 'full') {
      return '1 / -1';
    }

    if (!this.span || this.span <= 1) {
      return null;
    }

    return `span ${this.span}`;
  }

  get resolvedDisabled(): boolean {
    return this.control?.disabled ?? false;
  }

  get resolvedErrorMessage(): { message: string; params: any } {
    return this.errorMessage;
  }

  get resolvedHelpText(): string | null {
    const helpText = this.helpText ?? this.control?.helpText;
    if (!helpText) {
      return null;
    }

    if (typeof helpText === 'string') {
      return helpText;
    }

    return (helpText as HelpText).text ?? null;
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get errorId(): string {
    return getAstralFieldErrorId(this.resolvedInputId);
  }

  get helpId(): string {
    return getAstralFieldHelpId(this.resolvedInputId);
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const caption = this.control?.caption
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return caption || this.uid;
  }

  get resolvedInvalid(): boolean {
    return shouldShowAstralValidationError(this.control);
  }

  get resolvedLabel(): string {
    return this.control?.caption ?? '';
  }

  get resolvedReadonly(): boolean {
    return this.control?.isReadonly ?? false;
  }

  @HostListener('focus')
  focusHost() {
    this.input?.nativeElement.focus();
  }

  ngAfterViewInit(): void {
    if (this.focused) {
      this.input?.nativeElement.focus();
      this.isFocused = true;
      this.cd.detectChanges();
    }
  }

  handleBlur(): void {
    this.isFocused = false;
    this.onblur();
  }

  handleChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.valueChange.emit({
      formSnapshot: this.control?.parent ? Object.assign({}, this.control.parent.value) : null,
      newValue: checked,
      previousValue: !checked,
    });
  }

  handleClick(event: Event): void {
    if (!this.resolvedReadonly) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  override onControlSet(): void {
    super.onControlSet();
    this.controlSubscription.unsubscribe();
    this.controlSubscription = new Subscription();

    if (!this.control) {
      return;
    }

    this.controlSubscription.add(this.control.statusChanges.subscribe(() => this.cd.markForCheck()));
    this.controlSubscription.add(this.control.valueChanges.subscribe(() => this.cd.markForCheck()));
    this.controlSubscription.add(this.control.touchStateChanged?.subscribe(() => this.cd.markForCheck()));
    this.controlSubscription.add(this.control.validatorsChanged?.subscribe(() => this.cd.markForCheck()));
  }

  override ngOnDestroy(): void {
    this.controlSubscription.unsubscribe();
    super.ngOnDestroy();
  }

  handleLabelClick(event: MouseEvent): void {
    if (event.target === this.input?.nativeElement || this.resolvedDisabled || this.resolvedReadonly || !this.control) {
      return;
    }

    // The visible switch is a styled label; update the form control explicitly for pointer interaction.
    event.preventDefault();
    const previousValue = !!this.control.value;
    const newValue = !previousValue;
    this.control.setValue(newValue);
    this.control.markAsDirty();
    this.valueChange.emit({
      formSnapshot: this.control.parent ? Object.assign({}, this.control.parent.value) : null,
      newValue,
      previousValue,
    });
  }

  handleFocus(): void {
    this.isFocused = true;
    this.onfocus();
  }
}
